// Client-side connection to a PartyKit match room: one WebSocket, typed messages, tiny surface.
// UI layer only (src/net is outside the deterministic engine).

import { ENGINE_VERSION, PROTOCOL_VERSION, roomUrl, type ClientMsg, type ServerMsg } from './protocol';

export interface NetRoom {
  readonly roomCode: string;
  /** Our player id in this room (set once the server welcomes us). */
  readonly playerId: string;
  send(msg: ClientMsg): void;
  close(): void;
}

export interface RoomCallbacks {
  onMessage: (msg: ServerMsg) => void;
  /** `code` is the WebSocket close code (see protocol.ts: 4004 kicked, 4005 replaced by a duplicate tab). */
  onClose: (code?: number) => void;
}

/** Open a room connection and complete the hello/welcome handshake. Rejects on version refusal,
 *  connection failure, or a 6s timeout (e.g. the party server isn't running); a server refusal carries
 *  its protocol error code on the Error (`code`), so callers can react to e.g. a room-code collision.
 *  The player's equipped cosmetics ride along so their lobby seat card can show their real avatar +
 *  frame. `create` marks a freshly-minted code: the room refuses it if that code is already occupied. */
export function joinRoom(roomCode: string, playerName: string, cb: RoomCallbacks, cosmetic?: { avatar: string; frame: string }, token?: string, create?: boolean): Promise<NetRoom> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(roomUrl(roomCode));
    } catch (e) {
      reject(e instanceof Error ? e : new Error('connection failed'));
      return;
    }
    let playerId = '';
    let settled = false;
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        ws.close();
        reject(new Error('Could not reach the game server. Please try again in a moment.'));
      }
    }, 6000);

    const room: NetRoom = {
      roomCode,
      get playerId() {
        return playerId;
      },
      send(msg: ClientMsg) {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
      },
      close() {
        ws.close();
      },
    };

    ws.addEventListener('open', () => {
      room.send({ t: 'hello', protocol: PROTOCOL_VERSION, engine: ENGINE_VERSION, name: playerName, token: token ?? netToken(), avatar: cosmetic?.avatar, frame: cosmetic?.frame, create: create || undefined });
    });
    ws.addEventListener('message', (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMsg;
      } catch {
        return;
      }
      if (!settled) {
        // the first meaningful reply settles the join: welcome → resolved, error → rejected
        if (msg.t === 'welcome') {
          settled = true;
          clearTimeout(timeout);
          playerId = msg.you;
          resolve(room);
        } else if (msg.t === 'error') {
          settled = true;
          clearTimeout(timeout);
          ws.close();
          reject(Object.assign(new Error(msg.msg), { code: msg.code })); // callers may branch on the protocol code
          return;
        }
      }
      cb.onMessage(msg);
    });
    ws.addEventListener('close', (ev) => {
      if (!settled) {
        settled = true;
        clearTimeout(timeout);
        reject(new Error('The connection was closed before the room answered.'));
      } else {
        cb.onClose(ev.code);
      }
    });
    ws.addEventListener('error', () => {
      /* close fires right after; handled there */
    });
  });
}

/** This tab's stable player identity: survives a page refresh (so a seat can be reclaimed after an
 *  accidental reload), dies with the tab (so two tabs on one machine stay two distinct players). */
export function netToken(): string {
  let t = sessionStorage.getItem('wi.netToken');
  if (!t) {
    t = Array.from({ length: 4 }, () => Math.random().toString(36).slice(2, 10)).join('');
    sessionStorage.setItem('wi.netToken', t);
  }
  return t;
}

/** A short, friendly room code (shared verbally or via invite link). */
export function makeRoomCode(): string {
  const abc = 'abcdefghjkmnpqrstuvwxyz23456789'; // no 0/O/1/l/i lookalikes
  let s = '';
  for (let i = 0; i < 6; i++) s += abc[Math.floor(Math.random() * abc.length)];
  return s;
}
